import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE,
  type IssuesBulkUpdateProgress,
  type IssuesBulkUpdateResult,
} from '@github-ui/issues-bulk-actions/job-status'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {PercentageCircle} from '@github-ui/percentage-circle'
import {useMutation} from '@github-ui/react-query'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {IdentifiableListItem} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {IssuesListSelection} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import type {IssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ClockIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useCallback, useMemo, useRef, useState} from 'react'

import {RepoIssuesBulkAssignees, RepoIssuesBulkLabels, RepoIssuesBulkMarkAs} from '../components/RepoIssuesBulkActions'
import {RepoIssuesBulkIssueType, issueTypeAppliedToAll} from '../components/RepoIssuesBulkIssueType'
import {RepoIssuesBulkMilestone, milestoneAppliedToAll} from '../components/RepoIssuesBulkMilestone'
import {RepoIssuesBulkProjects} from '../components/RepoIssuesBulkProjects'
import {repoIssuesBulkOptimisticOperation} from '../helpers/repo-issues-bulk-optimistic-update'
import {
  startIssuesBulkUpdate,
  updateIssuesTabCounter,
  type RepoIssuesBulkOptimisticUpdate,
  type RepoIssuesBulkTarget,
  type RepoIssuesBulkUpdate,
} from '../helpers/repo-issues-bulk-update'

interface UseRepoIssuesBulkActionsArgs {
  owner: string
  repo: string
  currentPage: number
  query: string
  repositoryId: string
  viewerCanBulkUpdate: boolean
  totalCount: number
  issueRows: IssueListItem[]
  allRowsAreIssues: boolean
  hasProjectsEnabled: boolean
  issueTypesEnabled: boolean
  refreshIssues: () => Promise<{isError: boolean; error: unknown}>
  bulkJobPending: boolean
  onBulkJobStarted: (jobId: string) => void
  clearBulkJob: () => void
  selectionPolicy?: IssuesBulkSelectionPolicy
}

export interface IssuesBulkSelectionPolicy {
  scopeKey: string
  rows: readonly IdentifiableListItem[]
  maxLoadedRowsExclusive: number
  updateIssuesTabCounter: boolean
}

interface RepoIssuesBulkMutationVariables {
  target: RepoIssuesBulkTarget
  update: RepoIssuesBulkUpdate
  optimisticUpdate?: RepoIssuesBulkOptimisticUpdate
  issues: IssueListItem[]
  submittedCount: number
  submittedKey: string
  visibleIssueIds: string[]
  allMatching: boolean
  jobResult: PromiseWithResolvers<IssuesBulkUpdateResult>
  completion: {resolve: () => void}
}

export function useRepoIssuesBulkActions({
  owner,
  repo,
  currentPage,
  query,
  repositoryId,
  viewerCanBulkUpdate,
  totalCount,
  issueRows,
  allRowsAreIssues,
  hasProjectsEnabled,
  issueTypesEnabled,
  refreshIssues,
  bulkJobPending,
  onBulkJobStarted,
  clearBulkJob,
  selectionPolicy,
}: UseRepoIssuesBulkActionsArgs): {
  selection: IssuesListSelection | undefined
  bulkActionError: string | null
  clearBulkActionError: () => void
  onBulkJobCompleted: (jobId: string, result: IssuesBulkUpdateResult) => boolean
  onBulkJobError: (jobId: string, error: Error) => boolean
} {
  const visibleRowIds = useMemo(
    () => (selectionPolicy?.rows ?? issueRows).map(row => getListItemIdentity(row)),
    [issueRows, selectionPolicy?.rows],
  )
  const selectionKey =
    selectionPolicy?.scopeKey ?? `${owner}/${repo}:${currentPage}:${query}:${visibleRowIds.join(',')}`
  const [selectionState, setSelectionState] = useState<{key: string; ids: Set<string>; allMatching: boolean}>(() => ({
    key: selectionKey,
    ids: new Set(),
    allMatching: false,
  }))
  const visibleRowIdSet = useMemo(() => new Set(visibleRowIds), [visibleRowIds])
  const hasScopedSelection = selectionPolicy !== undefined
  const selectedIdsInScope = useMemo(
    () =>
      hasScopedSelection ? new Set([...selectionState.ids].filter(id => visibleRowIdSet.has(id))) : selectionState.ids,
    [hasScopedSelection, selectionState.ids, visibleRowIdSet],
  )
  const hasSelectionOutsideScope = selectedIdsInScope.size !== selectionState.ids.size
  if (selectionState.key !== selectionKey || hasSelectionOutsideScope) {
    setSelectionState({
      key: selectionKey,
      ids: selectionState.key === selectionKey ? selectedIdsInScope : new Set(),
      allMatching: false,
    })
  }
  const currentSelection = useMemo(
    () =>
      selectionState.key === selectionKey
        ? {
            ...selectionState,
            ids: selectedIdsInScope,
            allMatching: hasSelectionOutsideScope ? false : selectionState.allMatching,
          }
        : {key: selectionKey, ids: new Set<string>(), allMatching: false},
    [hasSelectionOutsideScope, selectedIdsInScope, selectionKey, selectionState],
  )
  const selectedIssueIds = useMemo(
    () => (currentSelection.allMatching ? new Set(visibleRowIds) : currentSelection.ids),
    [currentSelection, visibleRowIds],
  )
  const selectedCount = currentSelection.allMatching ? totalCount : selectedIssueIds.size
  const canSelect =
    viewerCanBulkUpdate &&
    Boolean(repositoryId) &&
    (selectionPolicy ? selectionPolicy.rows.length < selectionPolicy.maxLoadedRowsExclusive : allRowsAreIssues)
  const selectedPullRequest = selectionPolicy?.rows.some(
    row => row.itemType === 'pull_request' && selectedIssueIds.has(getListItemIdentity(row)),
  )
  const canBulkUpdate = canSelect && !selectedPullRequest
  const canSelectAllMatching =
    isFeatureEnabled('issues_react_bulk_actions_by_query') && !selectionPolicy && totalCount <= 1000
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)
  const rangeSelectionAnchorRef = useRef<string | null>(null)
  const [bulkActionError, setBulkActionError] = useState<string | null>(null)
  const {addPersistedToast, addToast, clearPersistedToast} = useToastContext()

  const selectedVisibleIssues = useMemo(
    () => issueRows.filter(issue => selectedIssueIds.has(getListItemIdentity(issue))),
    [issueRows, selectedIssueIds],
  )
  const commonLabels = useMemo(() => {
    const firstIssue = selectedVisibleIssues[0]
    if (!firstIssue) return []
    return firstIssue.labels
      .filter(label => selectedVisibleIssues.every(issue => issue.labels.some(candidate => candidate.id === label.id)))
      .map(label => ({id: String(label.id), name: label.name}))
  }, [selectedVisibleIssues])
  const commonAssignees = useMemo(() => {
    const firstIssue = selectedVisibleIssues[0]
    if (!firstIssue) return []
    return firstIssue.assignees
      .filter(assignee =>
        selectedVisibleIssues.every(issue => issue.assignees.some(candidate => candidate.id === assignee.id)),
      )
      .map(assignee => ({
        id: String(assignee.id),
        login: assignee.login,
        name: assignee.name,
        avatarUrl: assignee.avatarUrl,
        __typename: assignee.isCopilot || assignee.isAgent ? ('Bot' as const) : ('User' as const),
        profileResourcePath: assignee.profileResourcePath,
        isCopilot: assignee.isCopilot,
        isAgent: assignee.isAgent,
      }))
  }, [selectedVisibleIssues])
  const activeMilestone = useMemo(() => milestoneAppliedToAll(selectedVisibleIssues), [selectedVisibleIssues])
  const activeIssueType = useMemo(() => issueTypeAppliedToAll(selectedVisibleIssues), [selectedVisibleIssues])

  const reportBulkActionError = useCallback((message: string) => {
    setBulkActionError(message)
    announce(message, {assertive: true})
  }, [])
  const clearBulkActionError = useCallback(() => setBulkActionError(null), [])

  const reportBulkUpdateProgress = useCallback(
    ({state, percentage, failedCount}: IssuesBulkUpdateProgress) => {
      if (state === 'STARTED') {
        const currentPercentage = Math.min(Math.max(percentage, 0), 100)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({
          message: `Bulk update is in progress ${currentPercentage} % ${
            failedCount > 0 ? `(${failedCount} issues failing to update)` : ''
          }`,
          icon: <PercentageCircle progress={currentPercentage / 100} />,
          type: 'info',
          role: 'status',
        })
      } else {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({message: 'Bulk update is pending', icon: <ClockIcon />, type: 'info', role: 'status'})
      }
    },
    [addPersistedToast],
  )

  const optimisticRowCompletionsRef = useRef(new Map<string, Promise<void>>())
  const activeBulkJobRef = useRef<{
    jobId: string
    result: PromiseWithResolvers<IssuesBulkUpdateResult>
  } | null>(null)
  const {mutate: mutateOptimisticRow} = useEntityMutation<void>({
    mutationFn: async operation => {
      const completion = optimisticRowCompletionsRef.current.get(operation.id)
      if (!completion) throw new Error('Missing repository issue bulk update')
      await completion
    },
    getConfirmedOperation: () => undefined,
  })

  // Keep the mutation pending until refresh and selection updates finish,
  // so another bulk update cannot start too early.
  const {isPending: isBulkUpdatePending, mutate: mutateBulkUpdate} = useMutation({
    mutationFn: async ({target, update, jobResult}: RepoIssuesBulkMutationVariables) => {
      try {
        const jobId = await startIssuesBulkUpdate({target, update})
        activeBulkJobRef.current = {jobId, result: jobResult}
        onBulkJobStarted(jobId)
        reportBulkUpdateProgress({state: 'PENDING', percentage: 0, failedCount: 0})
        return await jobResult.promise
      } catch (error) {
        jobResult.reject(error)
        throw error
      }
    },
    onMutate: ({issues, update, allMatching}) => {
      if (selectionPolicy?.updateIssuesTabCounter !== false) {
        updateIssuesTabCounter({issues, update, failedIssueIds: new Set(), allMatching})
      }
    },
    onSuccess: async ({failedIssueNodeIds}, variables) => {
      const failedIds = new Set(failedIssueNodeIds)
      if (selectionPolicy?.updateIssuesTabCounter !== false) {
        updateIssuesTabCounter({
          issues: variables.issues.filter(issue => failedIds.has(getListItemIdentity(issue))),
          update: variables.update,
          failedIssueIds: new Set(),
          allMatching: variables.allMatching,
          direction: -1,
        })
      }
      clearPersistedToast()
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'success',
        message: `Bulk update completed ${failedIds.size > 0 ? 'with errors' : 'successfully'}`,
      })
      try {
        const refresh = await refreshIssues()
        if (refresh.isError) throw refresh.error
      } catch {
        reportBulkActionError(
          failedIds.size > 0
            ? `Could not update ${failedIds.size} of ${variables.submittedCount} issues`
            : 'Issues updated, but the results could not be refreshed',
        )
        return
      }

      setSelectionState(current =>
        current.key === variables.submittedKey
          ? {
              key: variables.submittedKey,
              ids: new Set(variables.visibleIssueIds.filter(id => failedIds.has(id))),
              allMatching: false,
            }
          : current,
      )
      if (failedIds.size > 0) {
        reportBulkActionError(`Could not update ${failedIds.size} of ${variables.submittedCount} issues`)
      } else {
        announce(`${variables.submittedCount} issue${variables.submittedCount === 1 ? '' : 's'} updated`)
      }
      requestAnimationFrame(() => selectAllCheckboxRef.current?.focus())
    },
    onError: async (error, variables) => {
      if (selectionPolicy?.updateIssuesTabCounter !== false) {
        updateIssuesTabCounter({
          issues: variables.issues,
          update: variables.update,
          failedIssueIds: new Set(),
          allMatching: variables.allMatching,
          direction: -1,
        })
      }
      clearPersistedToast()
      reportBulkActionError(
        error instanceof Error && error.message === ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE
          ? error.message
          : 'Could not update issues',
      )
      try {
        await refreshIssues()
      } catch {
        // The mutation error remains primary when the best-effort refresh also fails.
      }
      requestAnimationFrame(() => selectAllCheckboxRef.current?.focus())
    },
    onSettled: (_data, _error, variables) => {
      activeBulkJobRef.current = null
      clearBulkJob()
      variables.completion.resolve()
    },
  })

  const onBulkJobCompleted = useCallback((jobId: string, result: IssuesBulkUpdateResult) => {
    const activeJob = activeBulkJobRef.current
    if (!activeJob || activeJob.jobId !== jobId) return false

    activeJob.result.resolve(result)
    return true
  }, [])

  const onBulkJobError = useCallback((jobId: string, error: Error) => {
    const activeJob = activeBulkJobRef.current
    if (!activeJob || activeJob.jobId !== jobId) return false

    activeJob.result.reject(error)
    return true
  }, [])

  const handleBulkUpdate = useCallback(
    (update: RepoIssuesBulkUpdate, optimisticUpdate?: RepoIssuesBulkOptimisticUpdate) => {
      if (!canBulkUpdate) return
      const submittedCount = selectedCount
      const submittedIds = [...selectedIssueIds]
      const submittedKey = selectionKey
      const target = currentSelection.allMatching ? {query, repositoryId} : {ids: submittedIds}
      setBulkActionError(null)
      announce(`Updating ${submittedCount} issue${submittedCount === 1 ? '' : 's'}`)

      const jobResult = Promise.withResolvers<IssuesBulkUpdateResult>()
      const completion = Promise.withResolvers<void>()
      mutateBulkUpdate({
        target,
        update,
        optimisticUpdate,
        issues: selectedVisibleIssues,
        submittedCount,
        submittedKey,
        visibleIssueIds: visibleRowIds,
        allMatching: currentSelection.allMatching,
        jobResult,
        completion,
      })

      optimisticRowCompletionsRef.current.clear()
      for (const issue of selectedVisibleIssues) {
        const operation = repoIssuesBulkOptimisticOperation(issue, update, optimisticUpdate)
        const rowCompletion = (async () => {
          const {failedIssueNodeIds} = await jobResult.promise
          if (failedIssueNodeIds.includes(operation.id)) throw new Error('Repository issue bulk update failed')
          await completion.promise
        })()
        optimisticRowCompletionsRef.current.set(operation.id, rowCompletion)
        mutateOptimisticRow({operation})
      }
    },
    [
      currentSelection.allMatching,
      canBulkUpdate,
      mutateBulkUpdate,
      mutateOptimisticRow,
      query,
      repositoryId,
      selectedCount,
      selectedIssueIds,
      selectedVisibleIssues,
      selectionKey,
      visibleRowIds,
    ],
  )

  const bulkActions = useMemo<IssuesListSelection['actions']>(() => {
    if (!canBulkUpdate || selectedCount === 0) return undefined
    const commonProps = {
      disabled: isBulkUpdatePending || bulkJobPending,
      owner,
      repo,
      onUpdate: handleBulkUpdate,
    }
    const actions: NonNullable<IssuesListSelection['actions']> = [
      {key: 'mark-as', render: nested => <RepoIssuesBulkMarkAs {...commonProps} nested={nested} />},
      {
        key: 'labels',
        render: nested => <RepoIssuesBulkLabels {...commonProps} nested={nested} commonLabels={commonLabels} />,
      },
      {
        key: 'assignees',
        render: nested => (
          <RepoIssuesBulkAssignees
            {...commonProps}
            nested={nested}
            commonAssignees={commonAssignees}
            selectedIssues={selectedVisibleIssues}
          />
        ),
      },
      {
        key: 'milestone',
        render: nested => (
          <RepoIssuesBulkMilestone {...commonProps} nested={nested} activeMilestone={activeMilestone} />
        ),
      },
    ]
    if (hasProjectsEnabled) {
      actions.push({
        key: 'projects',
        render: nested => <RepoIssuesBulkProjects {...commonProps} nested={nested} issueIds={[...selectedIssueIds]} />,
      })
    }
    if (issueTypesEnabled) {
      actions.push({
        key: 'issue-type',
        render: nested => (
          <RepoIssuesBulkIssueType {...commonProps} nested={nested} activeIssueType={activeIssueType} />
        ),
      })
    }
    return actions
  }, [
    activeIssueType,
    activeMilestone,
    canBulkUpdate,
    commonAssignees,
    commonLabels,
    handleBulkUpdate,
    hasProjectsEnabled,
    isBulkUpdatePending,
    bulkJobPending,
    issueTypesEnabled,
    owner,
    repo,
    selectedCount,
    selectedIssueIds,
    selectedVisibleIssues,
  ])

  const selection = useMemo<IssuesListSelection | undefined>(() => {
    if (!canSelect) return undefined
    const allVisibleSelected = visibleRowIds.length > 0 && visibleRowIds.every(id => selectedIssueIds.has(id))
    return {
      selectedIssueIds,
      selectedCount,
      disabled: isBulkUpdatePending || bulkJobPending,
      selectAllCheckboxRef,
      onIssueSelect: (issueId, isSelected, isRangeSelection) => {
        const rangeSelectionAnchor = rangeSelectionAnchorRef.current
        setSelectionState(current => {
          const ids =
            current.key === selectionKey
              ? new Set(current.allMatching ? visibleRowIds : current.ids)
              : new Set<string>()
          const anchorIndex = rangeSelectionAnchor ? visibleRowIds.indexOf(rangeSelectionAnchor) : -1
          const issueIndex = visibleRowIds.indexOf(issueId)
          const idsToUpdate =
            isRangeSelection && anchorIndex >= 0 && issueIndex >= 0
              ? visibleRowIds.slice(Math.min(anchorIndex, issueIndex), Math.max(anchorIndex, issueIndex) + 1)
              : [issueId]
          for (const id of idsToUpdate) {
            if (isSelected) ids.add(id)
            else ids.delete(id)
          }
          return {key: selectionKey, ids, allMatching: false}
        })
        rangeSelectionAnchorRef.current = issueId
      },
      onToggleSelectAll: isSelected => {
        rangeSelectionAnchorRef.current = null
        setSelectionState({
          key: selectionKey,
          ids: isSelected ? new Set(visibleRowIds) : new Set(),
          allMatching: false,
        })
      },
      selectionAction:
        canSelectAllMatching &&
        allVisibleSelected &&
        totalCount > visibleRowIds.length &&
        !currentSelection.allMatching ? (
          <Button
            variant="invisible"
            size="small"
            onClick={() => setSelectionState({key: selectionKey, ids: new Set(), allMatching: true})}
          >
            Select all {totalCount.toLocaleString('en-US')} issues
          </Button>
        ) : undefined,
      actions: bulkActions,
    }
  }, [
    bulkActions,
    canSelect,
    canSelectAllMatching,
    currentSelection.allMatching,
    bulkJobPending,
    isBulkUpdatePending,
    selectedCount,
    selectedIssueIds,
    selectionKey,
    totalCount,
    visibleRowIds,
  ])

  return {
    selection,
    bulkActionError,
    clearBulkActionError,
    onBulkJobCompleted,
    onBulkJobError,
  }
}
